// La cabecera de cada sala, encima de su superficie y de su mismo ancho (visual.md §2.D, como una cabecera de sección de
// Cosas o del Finder, sin cápsula): a la izquierda el punto con el color de la sala, el nombre (abre sus ajustes), UNA
// pastilla con lo más urgente que le toca a quien mira (icono y número dentro: nunca «🔑 2 2») y «· N trabajando»
// (letrero.ts). El color de la sala vive solo en su punto.
//
// 04-11 (orden.md regla 16): «Ordenar» ya no va en cada sala: hay UNO para toda la oficina. Desde el 05-11 (#278) vive
// en la franja de los widgets (`BotonOrdenar`, BarraOrdenar.tsx): en la fila de las cabeceras parecía de la sala de la
// esquina.
import { ChevronUp, Pause, Settings2 } from 'lucide-react';
import { memo } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { aspectoGrupo } from '../marco/situacion';
import type { ZonaMundo } from './generar';
import type { ResumenLetrero } from './letrero';

/** El estilo de los botones fantasma de la escena (cabecera de sala): texto gris que se rellena al pasar. */
const FANTASMA =
  'flex items-center gap-1.5 rounded-o-s px-2 py-1 text-o-nota font-medium outline-none transition-colors duration-150 ease-o-apple focus-visible:ring-2 focus-visible:ring-oficina-enlace';

/** La pastilla de lo más urgente de una sala (icono y cuántos), la misma en su cabecera y en su carpeta. */
export function PastillaUrgente({ resumen, className }: { resumen: ResumenLetrero | undefined; className?: string }) {
  // El aspecto de SU grupo (el de la lista): una subida, el cohete dorado; el problema de Pau, en rojo.
  const urgente = resumen?.urgente ? aspectoGrupo(resumen.urgente.grupo) : null;
  const Icono = urgente?.icono;
  if (!resumen?.urgente || !urgente || !Icono) return null;
  return (
    <span className={cn('inline-flex h-5 shrink-0 items-center gap-1 rounded-full px-1.5 text-o-mini font-semibold tabular-nums', urgente.insignia, className)} data-testid="letrero-urgente">
      <Icono className="h-3 w-3" strokeWidth={2.5} fill={Icono === Pause ? 'currentColor' : 'none'} aria-hidden />
      {resumen.urgente.n}
    </span>
  );
}

export const Letrero = memo(function Letrero({
  zona,
  z,
  resumen,
  alAjustarSala,
  alPlegar,
}: {
  zona: ZonaMundo;
  z: number;
  resumen: ResumenLetrero | undefined;
  alAjustarSala: (salaId: string) => void;
  /** 04-11 — una secundaria abierta mientras las demás van plegadas (generar.ts `abierta`): «⌃» la vuelve a su carpeta. */
  alPlegar?: (salaId: string) => void;
}) {
  return (
    <div
      className="absolute flex items-center justify-between gap-2"
      style={{ left: zona.letrero.x * z, top: zona.letrero.y * z, width: zona.rect.w * z, height: (zona.rect.y - zona.letrero.y) * z }}
    >
      <button
        type="button"
        onClick={() => alAjustarSala(zona.projectId)}
        aria-label={[t('oficinaV2.sala.ajustes', { sala: zona.nombre }), resumen?.urgente?.texto, resumen?.trabajando?.texto].filter(Boolean).join(' · ')}
        className="group flex min-w-0 items-center gap-2 rounded-o-s px-1 py-0.5 text-o-texto font-semibold text-oficina-texto outline-none transition-colors duration-150 ease-o-apple hover:bg-oficina-control focus-visible:ring-2 focus-visible:ring-oficina-enlace"
      >
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: zona.color }} aria-hidden />
        <span className="truncate">{zona.nombre}</span>
        <PastillaUrgente resumen={resumen} />
        {resumen?.trabajando ? <span className="truncate text-o-sub font-normal text-oficina-texto2">· {resumen.trabajando.texto}</span> : null}
        <Settings2 className="h-3.5 w-3.5 shrink-0 text-oficina-texto2 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
      </button>
      {alPlegar ? (
        <button
          type="button"
          onClick={() => alPlegar(zona.projectId)}
          aria-label={t('oficinaV2.carpetas.plegar', { sala: zona.nombre })}
          title={t('oficinaV2.carpetas.plegar', { sala: zona.nombre })}
          className={cn(FANTASMA, 'shrink-0 text-oficina-texto2 hover:bg-oficina-control hover:text-oficina-texto')}
          data-testid="plegar-sala"
        >
          <ChevronUp className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
});
